'use client';
import React from 'react';
import Image from 'next/image';
import { PortableTextBlock } from '@portabletext/react';
import { LANDING_OR_PRODUCT_PAGE_QUERY_RESULT } from '@/sanity.types';

import './heroSection.scss';
import { urlForImage } from '@/sanity/lib/utils';
import { LandingPortableText } from '@/components/landingPortableText/landingPortableText';
import { imageData } from '@/config/defaultBlur';
import Button from '@/componentv2/button/button';
import Typography from '@/componentv2/typography/typography';
import { Container } from 'react-bootstrap';
import FontAwesomeIcon from '@/componentv2/fontAwesomeIcon/fontAwesomeIcon';

interface HeroSectionProps {
  data: NonNullable<LANDING_OR_PRODUCT_PAGE_QUERY_RESULT>;
}

export default function HeroSection({ data }: HeroSectionProps) {
  const heroContent = data?._type === 'productPage' ? data?.hero : null;

  // Get background style based on backgroundColor value
  const getBackgroundStyle = () => {
    if (!heroContent?.backgroundColor) return {};

    const bgValue = heroContent.backgroundColor;

    // Check if it's a gradient (starts with 'linear-gradient')
    if (bgValue.startsWith('linear-gradient')) {
      return { background: bgValue };
    }

    // Otherwise it's a solid color
    return { backgroundColor: bgValue };
  };

  return (
    <section className="new-product-hero-section" style={getBackgroundStyle()}>
      <Container>
        <div className="hero-content">
          <div className="hero-text">
            {heroContent?.catchphrase && (
              <div className="product-header">
                <LandingPortableText
                  value={heroContent?.catchphrase as PortableTextBlock[]}
                />
              </div>
            )}
            <div className="hero-title">
              <LandingPortableText
                value={heroContent?.productName as PortableTextBlock[]}
              />
            </div>
            {heroContent?.description && (
              <Typography style="paragraph" className="hero-description">
                {heroContent?.description}
              </Typography>
            )}
            <div className="hero-button-container">
              {heroContent?.button1?.text && (
                <Button
                  href={heroContent?.button1?.link}
                  className="hero-button"
                  variant="primary"
                >
                  {heroContent?.button1?.text}
                </Button>
              )}
              {heroContent?.button2?.text && (
                <Button
                  variant="primary-ghost"
                  href={heroContent?.button2?.link}
                >
                  {heroContent?.button2?.text}
                </Button>
              )}
            </div>

            <div className="hero-highlights">
              {heroContent?.features?.map((feature, index) => (
                <div key={index} className="hero-highlight">
                  <span className="highlight-icon">
                    <FontAwesomeIcon
                      name="fa-caret-right right-arrow"
                      style="fa-solid"
                      fontSize="18px"
                    />
                  </span>
                  <Typography as="p" style="microcopy">
                    {feature}
                  </Typography>
                </div>
              ))}
            </div>
          </div>

          <div className="hero-image">
            <Image
              src={
                heroContent?.logo
                  ? (urlForImage(heroContent?.logo)?.url() as string)
                  : ''
              }
              alt={heroContent?.logo?.alt || 'Hero image'}
              width={
                heroContent?.logo?.imgAsset?.metadata?.dimensions?.width || 600
              }
              height={
                heroContent?.logo?.imgAsset?.metadata?.dimensions?.height || 400
              }
              className="hero-img"
              placeholder="blur"
              priority
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              blurDataURL={
                heroContent?.logo?.imgAsset?.metadata?.lqip ||
                imageData.blurImage
              }
            />
          </div>
        </div>
      </Container>
    </section>
  );
}
